import { supabase } from './supabaseClient';
import { parseISO, format, isSameDay, addDays } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import debounce from 'lodash/debounce';

/**
 * supabaseService.ts
 * 
 * Core service functions for interacting with Supabase database.
 * 
 * Note: Service data fetching is handled by ServiceContext.tsx
 * This file focuses on appointment scheduling, availability, and helper functions.
 */

// Legacy types - kept for backward compatibility with testimonials/packages/gallery/appointments
export interface Testimonial {
  id: number;
  name: string;
  text: string;
  rating: number;
  date: string;
  service?: string;
}

export interface Package {
  id: number;
  name: string;
  description: string;
  price: string;
  duration: string;
  services: string[];
  image_url?: string;
}

export interface GalleryImage {
  id: number;
  url: string;
  alt: string;
  category: string;
}

export interface Appointment {
  id: number;
  user_id: string;
  service_id: number;
  staff_id?: number;
  date: string;
  start_time: string;
  end_time: string;
  status: 'booked' | 'completed' | 'cancelled';
  notes?: string;
}

// Testimonials
export async function getTestimonials() {
  const { data, error } = await supabase
    .from('testimonials')
    .select('*')
    .order('date', { ascending: false });
    
  if (error) {
    console.error('Error fetching testimonials:', error);
    return [];
  }
  
  return data as Testimonial[];
}

// Packages
export async function getPackages() {
  const { data, error } = await supabase
    .from('packages')
    .select('*')
    .order('name');
    
  if (error) {
    console.error('Error fetching packages:', error);
    return [];
  }
  
  return data as Package[];
}

// Gallery
export async function getGalleryImages(category?: string) {
  let query = supabase
    .from('gallery')
    .select('*');
    
  if (category) {
    query = query.eq('category', category);
  }
  
  const { data, error } = await query.order('id');
    
  if (error) {
    console.error('Error fetching gallery images:', error);
    return [];
  }
  
  return data as GalleryImage[];
}

// Appointments
export async function getUserAppointments(userId: string) {
  const { data, error } = await supabase
    .from('appointments')
    .select(`
      *,
      services:service_id(id, name, duration, price)
    `)
    .eq('user_id', userId)
    .order('date', { ascending: true });
    
  if (error) {
    console.error('Error fetching user appointments:', error);
    return [];
  }
  
  return data;
}

export async function createAppointment(appointment: Omit<Appointment, 'id'>) {
  const { data, error } = await supabase
    .from('appointments')
    .insert(appointment)
    .select()
    .single();
    
  if (error) {
    console.error('Error creating appointment:', error);
    throw error;
  }
  
  return data;
}

export async function updateAppointment(id: number, updates: Partial<Appointment>) {
  const { data, error } = await supabase
    .from('appointments')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
    
  if (error) {
    console.error(`Error updating appointment ${id}:`, error);
    throw error;
  }
  
  return data;
}

export async function cancelAppointment(id: number) {
  return updateAppointment(id, { status: 'cancelled' });
}

// User profile
export async function getUserProfile(userId: string) {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();
    
  if (error) {
    console.error(`Error fetching user profile for ${userId}:`, error);
    return null;
  }
  
  return data;
}

export async function updateUserProfile(userId: string, updates: any) {
  const { data, error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('id', userId)
    .select()
    .single();
    
  if (error) {
    console.error(`Error updating user profile for ${userId}:`, error);
    throw error;
  }
  
  return data;
}

// For admin content management
export async function getContent(type: string) {
  const { data, error } = await supabase
    .from('content')
    .select('*')
    .eq('type', type);
    
  if (error) {
    console.error(`Error fetching content of type ${type}:`, error);
    return null;
  }
  
  return data;
}

export async function updateContent(id: number, updates: any) {
  const { data, error } = await supabase
    .from('content')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
    
  if (error) {
    console.error(`Error updating content ${id}:`, error);
    throw error;
  }
  
  return data;
}

// Simple in-memory cache for time slots
const timeSlotCache: {
  [key: string]: {
    slots: string[];
    timestamp: number;
    expiryMs: number;
  }
} = {};

// CONSTANT FOR TIMEZONE
const TEXAS_TIMEZONE = 'America/Chicago';

/**
 * Get available time slots for a staff member on a given date, considering total duration needed (in minutes).
 * Returns an array of time strings (e.g., ['09:00:00', '09:30:00', ...])
 */
export async function getAvailableTimeSlotsForStaff(
  staffId: string, 
  date: Date, 
  totalDuration: number,
  forceRefresh = false
): Promise<string[]> {
  try {
    const dateStr = format(date, 'yyyy-MM-dd');
    const cacheKey = `${staffId}-${dateStr}-${totalDuration}`;
    const cacheExpiryTime = 5 * 60 * 1000; // 5 minutes cache
    
    // Return cached data if available and not expired
    if (
      !forceRefresh && 
      timeSlotCache[cacheKey] && 
      Date.now() - timeSlotCache[cacheKey].timestamp < timeSlotCache[cacheKey].expiryMs
    ) {
      console.log(`[getAvailableTimeSlotsForStaff] Using cached data for ${cacheKey}`);
      return timeSlotCache[cacheKey].slots;
    }

    // Check if totalDuration is valid
    if (totalDuration <= 0) {
      console.warn("[getAvailableTimeSlotsForStaff] Invalid duration, must be > 0");
      return [];
    }

    const startHour = 9; // 9 AM
    const endHour = 19;  // 7 PM (slots must START before 7 PM, and END by 7 PM)
    const slotInterval = 30; // minutes

    // Fetch appointments for the staff member on the given date
    const { data: bookings, error: bookingsError } = await supabase
      .from('appointments')
      .select('id, start_time, end_time, status')
      .eq('staff_id', staffId)
      .gte('start_time', `${dateStr}T00:00:00.000Z`)
      .lt('end_time', `${dateStr}T23:59:59.999Z`)
      .neq('status', 'cancelled');

    if (bookingsError) {
      console.error('[getAvailableTimeSlotsForStaff] Error fetching staff bookings:', bookingsError);
      return [];
    }

    // Fetch explicit staff blocked times
    const { data: staffBlocks, error: staffBlocksError } = await supabase
      .from('staff_availability')
      .select('id, specific_date, start_time, end_time, notes')
      .eq('staff_id', staffId)
      .eq('specific_date', dateStr);

    if (staffBlocksError) {
      console.error('[getAvailableTimeSlotsForStaff] Error fetching staff blocked times:', staffBlocksError);
    }

    // Create blocked time ranges in Texas timezone
    const blockedRangesInTexas: { start: Date; end: Date }[] = [];
    
    // Add booked appointments to blocked ranges
    if (bookings) {
      for (const booking of bookings) {
        if (!booking.start_time || !booking.end_time) continue;
        
        // Ensure 'Z' is appended if times are UTC but don't include it in the string
        const startTimeStr = booking.start_time.endsWith('Z') ? booking.start_time : `${booking.start_time}Z`;
        const endTimeStr = booking.end_time.endsWith('Z') ? booking.end_time : `${booking.end_time}Z`;

        const utcBookingStart = parseISO(startTimeStr);
        const utcBookingEnd = parseISO(endTimeStr);
        const txBookingStart = toZonedTime(utcBookingStart, TEXAS_TIMEZONE);
        const txBookingEnd = toZonedTime(utcBookingEnd, TEXAS_TIMEZONE);

        blockedRangesInTexas.push({ start: txBookingStart, end: txBookingEnd });
      }
    }

    // Add staff blocked times to ranges
    if (staffBlocks) {
      for (const block of staffBlocks) {
        if (!block.start_time || !block.end_time || !block.specific_date) continue;
        
        const utcBlockStart = parseISO(`${block.specific_date}T${block.start_time}Z`);
        const utcBlockEnd = parseISO(`${block.specific_date}T${block.end_time}Z`);
        const txBlockStart = toZonedTime(utcBlockStart, TEXAS_TIMEZONE);
        const txBlockEnd = toZonedTime(utcBlockEnd, TEXAS_TIMEZONE);
        
        blockedRangesInTexas.push({ start: txBlockStart, end: txBlockEnd });
      }
    }

    // Calculate available slots
    const availableSlots: string[] = [];
    const nowInTexas = toZonedTime(new Date(), TEXAS_TIMEZONE);
    const todayDateStartInTexas = new Date(
      nowInTexas.getFullYear(), 
      nowInTexas.getMonth(), 
      nowInTexas.getDate()
    );

    // Get components of the selected date
    const selectedDateYear = date.getFullYear();
    const selectedDateMonth = date.getMonth();
    const selectedDateDay = date.getDate();

    // Generate and check time slots
    for (let hour = startHour; hour < endHour; hour++) {
      for (let minute = 0; minute < 60; minute += slotInterval) {
        const localSlotDateTime = new Date(selectedDateYear, selectedDateMonth, selectedDateDay, hour, minute, 0);
        const slotStartTexas = toZonedTime(localSlotDateTime, TEXAS_TIMEZONE);
        const slotEndTexas = new Date(slotStartTexas.getTime() + totalDuration * 60000);

        // Skip slots in the past for today
        if (
          isSameDay(slotStartTexas, todayDateStartInTexas) && 
          slotStartTexas < nowInTexas
        ) {
          continue;
        }
        
        // Skip slots that end after working hours
        const localWorkingDayEnd = new Date(selectedDateYear, selectedDateMonth, selectedDateDay, endHour, 0, 0);
        const workingDayEndTexas = toZonedTime(localWorkingDayEnd, TEXAS_TIMEZONE);
        if (slotEndTexas > workingDayEndTexas) {
          continue;
        }

        // Check if slot overlaps with any blocked range
        let isOverlapped = false;
        for (const range of blockedRangesInTexas) {
          if (slotStartTexas < range.end && slotEndTexas > range.start) {
            isOverlapped = true;
            break;
          }
        }

        // If not overlapped, add to available slots
        if (!isOverlapped) {
          availableSlots.push(format(slotStartTexas, 'HH:mm:ss'));
        }
      }
    }

    // Add to cache
    timeSlotCache[cacheKey] = {
      slots: availableSlots,
      timestamp: Date.now(),
      expiryMs: cacheExpiryTime
    };

    return availableSlots;
  } catch (err) {
    console.error('[getAvailableTimeSlotsForStaff] Unexpected error:', err);
    return [];
  }
}

/**
 * Preload time slots for dates adjacent to the given date to improve perceived performance
 */
export async function preloadAdjacentDates(
  staffId: string,
  date: Date,
  totalDuration: number
): Promise<void> {
  try {
    const tomorrow = addDays(date, 1);
    const yesterday = addDays(date, -1);
    
    // Don't await these - let them load in background
    // Wrap in try/catch to handle errors silently
    try {
      getAvailableTimeSlotsForStaff(staffId, tomorrow, totalDuration);
    } catch (e) {
      console.error('[preloadAdjacentDates] Error preloading tomorrow:', e);
    }
    
    try {
      getAvailableTimeSlotsForStaff(staffId, yesterday, totalDuration);
    } catch (e) {
      console.error('[preloadAdjacentDates] Error preloading yesterday:', e);
    }
  } catch (error) {
    console.error('[preloadAdjacentDates] Error:', error);
    // Silently fail - this is a performance optimization, not critical functionality
  }
}

/**
 * Fetch with retry functionality for better error resilience
 */
export async function fetchWithRetry<T>(
  fetchFn: () => Promise<T>,
  maxRetries = 3,
  delayMs = 1000
): Promise<T> {
  let retries = 0;
  while (true) {
    try {
      return await fetchFn();
    } catch (error) {
      retries++;
      if (retries >= maxRetries) throw error;
      console.log(`Retry ${retries}/${maxRetries} after ${delayMs}ms...`);
      await new Promise(resolve => setTimeout(resolve, delayMs * retries)); // Exponential backoff
    }
  }
}